import {
  ACCOUNTING_FOLLOWUP_PAGE_SIZES,
  ACCOUNTING_FOLLOWUP_SORT_FIELDS,
  AccountingAuditRow,
  AccountingEmailReminderDraft,
  AccountingEmailReminderSendResult,
  AccountingExpenseVisibility,
  AccountingFollowUpReminder,
  AccountingFollowUpSortField,
  AccountingImpayesBucket,
  AccountingInvoiceFollowUp,
  AccountingInvoiceFollowUpPagination,
  AccountingInvoiceFollowUpStats,
  AccountingListQuery,
  AccountingOutstandingReportView,
  AccountingSummary,
  DEFAULT_ACCOUNTING_EXPENSE_VISIBILITY,
  AccountingOutstandingRow,
  AccountingOutstandingTotals,
  AccountingOverdueStatus,
  AccountingReportEnvelope,
  AccountingReportFormat,
  AccountingReportType,
  AccountingScopeKind,
  AccountingScopeQuery,
  AccountingAttachInvoicePayload,
  AccountingSupplierEmailAvailability,
  AccountingSupplierLookupItem,
} from '../../../models/accounting'

export const DEFAULT_ACCOUNTING_ACADEMIC_YEAR = '2026-2027'
export const ACCOUNTING_SCOPE_STORAGE_KEY = 'maarefati.accounting.scope'
export const ACCOUNTING_EXPENSE_MANAGEMENT_SCOPE_STORAGE_KEY =
  'maarefati.accounting.expenseManagement.establishment'
export const ACCOUNTING_MODULE = 'MAAREFATI_SCHOOL_ACCOUNTING'

function unwrapAccountingIdentifier(value: unknown, depth = 0): string {
  if (value == null || value === '' || depth > 3) return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim()
  if (typeof value === 'object') {
    const rec = value as {$oid?: unknown; _id?: unknown; id?: unknown; toHexString?: () => unknown}
    if (typeof rec.$oid === 'string' && rec.$oid.trim()) return rec.$oid.trim()
    if (typeof rec.toHexString === 'function') {
      const hex = rec.toHexString()
      if (hex != null && hex !== '') return String(hex).trim()
    }
    if (rec._id != null && rec._id !== value) return unwrapAccountingIdentifier(rec._id, depth + 1)
    if (rec.id != null && rec.id !== value) return unwrapAccountingIdentifier(rec.id, depth + 1)
  }
  return ''
}

export function accountingEstablishmentIdOf(value: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim()
  if (typeof value === 'object') {
    const rec = value as {_id?: unknown; id?: unknown}
    const raw = rec._id != null && rec._id !== '' ? rec._id : rec.id
    return unwrapAccountingIdentifier(raw)
  }
  return ''
}

export function accountingEstablishmentsForRole<T extends {_id?: string}>(input: {
  catalog: T[]
  linked: unknown[]
  allAccess: boolean
}): T[] {
  const byId = new Map<string, T>()
  for (const item of input.catalog || []) {
    const id = accountingEstablishmentIdOf(item)
    if (id) byId.set(id, item)
  }
  for (const value of input.linked || []) {
    const id = accountingEstablishmentIdOf(value)
    if (!id || byId.has(id)) continue
    if (value && typeof value === 'object') byId.set(id, value as T)
  }
  const merged = Array.from(byId.values())
  if (input.allAccess) return merged
  const allowed = new Set((input.linked || []).map(accountingEstablishmentIdOf).filter(Boolean))
  return merged.filter((item) => allowed.has(accountingEstablishmentIdOf(item)))
}

export function isAccountingEstablishmentActive(value: unknown): boolean {
  if (value == null) return false
  if (typeof value !== 'object') return true
  const rec = value as {isActive?: unknown; isDeleted?: unknown; active?: unknown; deleted?: unknown}
  if (rec.isDeleted === true || rec.deleted === true) return false
  if (rec.isActive === false || rec.active === false) return false
  return true
}

export function accountingActiveCatalogEstablishments<T>(catalog: T[] | undefined | null): T[] {
  const byId = new Map<string, T>()
  for (const item of catalog || []) {
    const id = accountingEstablishmentIdOf(item)
    if (!id || !isAccountingEstablishmentActive(item)) continue
    byId.set(id, item)
  }
  return Array.from(byId.values())
}

export function isAccountingEstablishmentLinked(
  establishmentId: unknown,
  linked: unknown[] | undefined | null
): boolean {
  const id = accountingEstablishmentIdOf(establishmentId)
  if (!id) return false
  return (linked || []).some((item) => accountingEstablishmentIdOf(item) === id)
}

export function accountingExpenseCreatedId(result: unknown, fallback?: string): string {
  const root = (result as {payload?: unknown} | null) || null
  const payload = (root && 'payload' in (root as object) ? (root as {payload?: unknown}).payload : result) as
    | {expense?: {_id?: unknown; id?: unknown}; id?: unknown; _id?: unknown}
    | undefined
  const expense = payload?.expense
  const raw = expense?.id ?? expense?._id ?? payload?.id ?? payload?._id ?? fallback
  return raw != null && raw !== '' ? String(raw) : ''
}

export function accountingExpenseCreatedReference(result: unknown): string {
  const root = (result as {payload?: unknown} | null) || null
  const payload = (root && 'payload' in (root as object) ? (root as {payload?: unknown}).payload : result) as
    | {expense?: {reference?: unknown; expenseReference?: unknown}; reference?: unknown; expenseReference?: unknown}
    | undefined
  const expense = payload?.expense
  const raw =
    expense?.reference ?? expense?.expenseReference ?? payload?.expenseReference ?? payload?.reference
  return raw != null && raw !== '' ? String(raw) : ''
}

export function accountingCreateExpenseErrorMessageId(errorCode?: string, status?: number): string {
  const code = String(errorCode || '')
  if (code === 'ESTABLISHMENT_NOT_FOUND') return 'ACCOUNTING.EXPENSES.ERR_ESTABLISHMENT_NOT_FOUND'
  if (code === 'ESTABLISHMENT_INACTIVE') return 'ACCOUNTING.EXPENSES.ERR_ESTABLISHMENT_INACTIVE'
  if (code === 'ESTABLISHMENT_DELETED') return 'ACCOUNTING.EXPENSES.ERR_ESTABLISHMENT_DELETED'
  if (code === 'IDEMPOTENCY_KEY_CONFLICT') return 'ACCOUNTING.EXPENSES.ERR_IDEMPOTENCY'
  if (code === 'PERMISSION_DENIED' || code === 'ROLE_FORBIDDEN') {
    return 'ACCOUNTING.EXPENSES.ERR_PERMISSION'
  }
  if (code === 'ESTABLISHMENT_NOT_LINKED') return 'ACCOUNTING.EXPENSES.ERR_ESTABLISHMENT_NOT_LINKED'
  if (status === 400) return 'ACCOUNTING.EXPENSES.ERR_ESTABLISHMENT_INVALID'
  if (status === 403) return 'ACCOUNTING.EXPENSES.ERR_PERMISSION'
  if (status === 404) return 'ACCOUNTING.EXPENSES.ERR_ESTABLISHMENT_NOT_FOUND'
  if (status === 409) return 'ACCOUNTING.EXPENSES.ERR_ESTABLISHMENT_INACTIVE'
  if (!status) return 'ACCOUNTING.EXPENSES.ERR_NETWORK'
  return 'ACCOUNTING.ERROR'
}

export interface AccountingExpenseManagementEstablishmentChoice {
  scope: AccountingScopeKind
  establishmentId: string
}

export function defaultExpenseManagementEstablishment(): AccountingExpenseManagementEstablishmentChoice {
  return {scope: 'GROUP', establishmentId: ''}
}

export function loadPersistedExpenseManagementEstablishment(): AccountingExpenseManagementEstablishmentChoice {
  if (typeof sessionStorage === 'undefined') return defaultExpenseManagementEstablishment()
  try {
    const raw = sessionStorage.getItem(ACCOUNTING_EXPENSE_MANAGEMENT_SCOPE_STORAGE_KEY)
    if (!raw) return defaultExpenseManagementEstablishment()
    const parsed = JSON.parse(raw) as Partial<AccountingExpenseManagementEstablishmentChoice>
    return {
      scope: parsed.scope === 'ESTABLISHMENT' ? 'ESTABLISHMENT' : 'GROUP',
      establishmentId: parsed.establishmentId ? String(parsed.establishmentId) : '',
    }
  } catch {
    return defaultExpenseManagementEstablishment()
  }
}

export function persistExpenseManagementEstablishment(
  choice: AccountingExpenseManagementEstablishmentChoice
): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(ACCOUNTING_EXPENSE_MANAGEMENT_SCOPE_STORAGE_KEY, JSON.stringify(choice))
}

export function buildAccountingExpenseManagementQuery(input: {
  filters: PersistedAccountingScope
  establishment: AccountingExpenseManagementEstablishmentChoice
  catalog?: unknown[]
}): AccountingScopeQuery {
  const requestedId = accountingEstablishmentIdOf(input.establishment.establishmentId)
  const hasEstablishment = input.establishment.scope === 'ESTABLISHMENT' && Boolean(requestedId)
  const scope: AccountingScopeKind = hasEstablishment ? 'ESTABLISHMENT' : 'GROUP'
  return {
    scope,
    establishmentId: scope === 'ESTABLISHMENT' ? requestedId : undefined,
    academicYear: input.filters.academicYear || defaultAccountingScope().academicYear,
    from: isCompleteAccountingDate(input.filters.dateFrom) ? input.filters.dateFrom : undefined,
    to: isCompleteAccountingDate(input.filters.dateTo) ? input.filters.dateTo : undefined,
    paymentMethod: input.filters.paymentMethod || undefined,
  }
}

export function accountingCreateExpenseFileField(
  documentType?: string | null
): 'invoice' | 'bl' | null {
  const type = String(documentType || '').toUpperCase()
  if (type === 'BL') return 'bl'
  if (type === 'FACTURE') return 'invoice'
  return null
}

export function buildAccountingCreateExpenseFormData(
  payload: Record<string, unknown>,
  file?: File | null
): FormData {
  const form = new FormData()
  const skip = new Set(['scope', 'from', 'to', 'academicYear', 'file', 'invoice', 'bl'])
  Object.keys(payload).forEach((key) => {
    if (skip.has(key)) return
    const value = payload[key]
    if (value === undefined || value === null || value === '') return
    if (typeof File !== 'undefined' && value instanceof File) return
    form.append(key, typeof value === 'number' ? String(value) : String(value))
  })
  const field = accountingCreateExpenseFileField(String(payload.documentType || ''))
  if (file && field) form.append(field, file)
  if (payload.invoice && typeof File !== 'undefined' && payload.invoice instanceof File) {
    form.append('invoice', payload.invoice)
  }
  if (payload.bl && typeof File !== 'undefined' && payload.bl instanceof File) {
    form.append('bl', payload.bl)
  }
  return form
}

export function isAccountingCreateExpenseDuplicate(result: unknown): boolean {
  const root = result as {payload?: {duplicate?: unknown}; duplicate?: unknown} | null
  return root?.payload?.duplicate === true || root?.duplicate === true
}

export interface PersistedAccountingScope {
  scope: AccountingScopeKind
  establishmentId: string
  academicYear: string
  dateFrom: string
  dateTo: string
  paymentMethod: string
}

export function formatAccountingDisplayDate(value?: string | null): string {
  if (!value) return '—'
  const raw = String(value).slice(0, 10)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  if (!match) return raw
  return `${match[3]}/${match[2]}/${match[1]}`
}

export function isAccountingExpenseDeleted(row: {
  status?: string | null
  isDeleted?: boolean
  deleted?: boolean
  deletedAt?: string | null
}): boolean {
  if (row.isDeleted === true || row.deleted === true || row.deletedAt) return true
  return String(row.status || '').toUpperCase() === 'DELETED'
}

export function applyAccountingExpenseVisibility(
  query: AccountingListQuery,
  visibility: AccountingExpenseVisibility = DEFAULT_ACCOUNTING_EXPENSE_VISIBILITY
): AccountingListQuery {
  const next: AccountingListQuery = {...query, visibility}
  if (visibility === 'ACTIVE') {
    next.excludeStatus = 'CANCELLED'
    next.includeDeleted = false
    next.deletedOnly = false
    const status = String(next.status || '').toUpperCase()
    if (!status || status === 'CANCELLED' || status === 'DELETED') {
      delete next.status
    }
    return next
  }
  if (visibility === 'CANCELLED') {
    next.status = 'CANCELLED'
    next.includeDeleted = false
    next.deletedOnly = false
    delete next.excludeStatus
    return next
  }
  if (visibility === 'DELETED') {
    next.includeDeleted = true
    next.deletedOnly = true
    delete next.excludeStatus
    return next
  }
  next.includeDeleted = true
  next.deletedOnly = false
  delete next.excludeStatus
  return next
}

export function expenseVisibleForAccountingList(
  row: {
    status?: string | null
    isDeleted?: boolean
    deleted?: boolean
    deletedAt?: string | null
  },
  visibility: AccountingExpenseVisibility = DEFAULT_ACCOUNTING_EXPENSE_VISIBILITY
): boolean {
  const cancelled = String(row.status || '').toUpperCase() === 'CANCELLED'
  const deleted = isAccountingExpenseDeleted(row)
  if (visibility === 'CANCELLED') return cancelled && !deleted
  if (visibility === 'DELETED') return deleted
  if (visibility === 'ALL') return true
  return !cancelled && !deleted
}

export function accountingExpenseOperationalKpis(summary: AccountingSummary | null | undefined) {
  const supplier = summary?.supplierAccounting
  const depenses = summary?.depenses
  return {
    totalTtc: supplier?.totalExpensesTtc ?? depenses?.recordedTtc,
    netPayable: supplier?.totalSupplierNetPayable,
    withholding: supplier?.withholdingAmountsPendingRemittance,
    missingInvoices: supplier?.missingSupplierInvoices,
  }
}

export function accountingTejOperationalKpis(summary: AccountingSummary | null | undefined) {
  const supplier = summary?.supplierAccounting
  return {
    trackingCount: supplier?.pendingTejCertificates,
    withholdingAmount: supplier?.withholdingAmountsPendingRemittance,
    toComplete: supplier?.pendingTejCertificates,
    declaredCount: undefined as number | undefined,
  }
}

export function formatAccountingTnd(value: number | string | null | undefined): string {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return '0,000 DT'
  const rounded = Math.round(numeric * 1000) / 1000
  const [intPart, decPart] = rounded.toFixed(3).split('.')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${grouped},${decPart} DT`
}

export function accountingMetricValue(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string') {
    const numeric = Number(value.replace(/\s/g, '').replace(',', '.'))
    return Number.isFinite(numeric) ? numeric : undefined
  }
  return undefined
}

export function formatAccountingMetric(value: unknown, unavailable: string): string {
  const numeric = accountingMetricValue(value)
  if (numeric === undefined) return unavailable
  return formatAccountingTnd(numeric)
}

export function formatAccountingCount(value: unknown, unavailable: string): string {
  const numeric = accountingMetricValue(value)
  if (numeric === undefined) return unavailable
  return String(Math.trunc(numeric))
}

export function unallocatedReceiptsTotal(impayes?: AccountingImpayesBucket | null): number | undefined {
  if (!impayes) return undefined
  const raw = impayes.unallocatedReceipts
  if (typeof raw === 'number' || typeof raw === 'string') return accountingMetricValue(raw)
  if (raw && typeof raw === 'object') return accountingMetricValue(raw.total)
  return undefined
}

export function newAccountingIdempotencyKey(prefix: string): string {
  const rand = Math.random().toString(36).slice(2, 12)
  return `${prefix}-${Date.now()}-${rand}`
}

export function defaultAccountingScope(): PersistedAccountingScope {
  return {
    scope: 'GROUP',
    establishmentId: '',
    academicYear: DEFAULT_ACCOUNTING_ACADEMIC_YEAR,
    dateFrom: '',
    dateTo: '',
    paymentMethod: '',
  }
}

export function loadPersistedAccountingScope(): PersistedAccountingScope {
  if (typeof sessionStorage === 'undefined') return defaultAccountingScope()
  try {
    const raw = sessionStorage.getItem(ACCOUNTING_SCOPE_STORAGE_KEY)
    if (!raw) return defaultAccountingScope()
    const parsed = JSON.parse(raw) as Partial<PersistedAccountingScope>
    return {
      ...defaultAccountingScope(),
      ...parsed,
      scope: parsed.scope === 'ESTABLISHMENT' ? 'ESTABLISHMENT' : 'GROUP',
      academicYear: parsed.academicYear || DEFAULT_ACCOUNTING_ACADEMIC_YEAR,
    }
  } catch {
    return defaultAccountingScope()
  }
}

export function persistAccountingScope(scope: PersistedAccountingScope): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(ACCOUNTING_SCOPE_STORAGE_KEY, JSON.stringify(scope))
}

export function isCompleteAccountingDate(value?: string | null): boolean {
  if (!value) return false
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const year = Number(value.slice(0, 4))
  if (year < 2000 || year > 2100) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime())
}

function appendParam(params: URLSearchParams, key: string, value: unknown) {
  if (value === undefined || value === null || value === '') return
  params.set(key, String(value))
}

export function buildAccountingScopeParams(query: AccountingScopeQuery = {}): URLSearchParams {
  const params = new URLSearchParams()
  const scope: AccountingScopeKind = query.scope === 'ESTABLISHMENT' ? 'ESTABLISHMENT' : 'GROUP'
  appendParam(params, 'scope', scope)
  if (scope === 'ESTABLISHMENT') {
    appendParam(params, 'establishmentId', query.establishmentId)
  }
  appendParam(params, 'academicYear', query.academicYear)
  if (isCompleteAccountingDate(query.from)) appendParam(params, 'dateFrom', query.from)
  if (isCompleteAccountingDate(query.to)) appendParam(params, 'dateTo', query.to)
  appendParam(params, 'paymentMethod', query.paymentMethod)
  return params
}

export function buildAccountingListParams(query: AccountingListQuery = {}): URLSearchParams {
  const params = buildAccountingScopeParams(query)
  appendParam(params, 'page', query.page)
  appendParam(params, 'pageNumber', query.page)
  appendParam(params, 'limit', query.limit)
  appendParam(params, 'pageCount', query.limit)
  appendParam(params, 'search', query.search)
  appendParam(params, 'ticketNumber', query.ticketNumber)
  appendParam(params, 'sort', query.sort)
  if (query.sort && query.sort.includes(':')) {
    const [sortField, sortOrder] = query.sort.split(':')
    appendParam(params, 'sortField', sortField)
    appendParam(params, 'sortOrder', sortOrder)
  }
  appendParam(params, 'sortBy', query.sortBy)
  appendParam(params, 'sortOrder', query.sortOrder)
  appendParam(params, 'status', query.status)
  appendParam(params, 'category', query.category)
  appendParam(params, 'documentType', query.documentType)
  appendParam(params, 'invoiceStatus', query.invoiceStatus)
  if (query.missingInvoice === true || query.missingInvoice === 'true') {
    appendParam(params, 'missingInvoice', 'true')
  }
  appendParam(params, 'withholdingReviewStatus', query.withholdingReviewStatus)
  appendParam(params, 'tejStatus', query.tejStatus)
  appendParam(params, 'supplierName', query.supplierName)
  appendParam(params, 'supplierInvoiceNumber', query.supplierInvoiceNumber)
  appendParam(params, 'deliveryNoteNumber', query.deliveryNoteNumber)
  appendParam(params, 'internalReference', query.internalReference)
  appendParam(params, 'minAmountTtc', query.minAmountTtc)
  appendParam(params, 'maxAmountTtc', query.maxAmountTtc)
  appendParam(params, 'visibility', query.visibility)
  appendParam(params, 'excludeStatus', query.excludeStatus)
  appendParam(params, 'excludeExpenseStatus', query.excludeExpenseStatus)
  if (query.includeCancelledExpenses === true) {
    appendParam(params, 'includeCancelledExpenses', 'true')
  } else if (query.includeCancelledExpenses === false) {
    appendParam(params, 'includeCancelledExpenses', 'false')
  }
  if (query.includeDeleted === true || query.includeDeleted === 'true') {
    appendParam(params, 'includeDeleted', 'true')
  } else if (query.includeDeleted === false || query.includeDeleted === 'false') {
    appendParam(params, 'includeDeleted', 'false')
  }
  if (query.deletedOnly === true || query.deletedOnly === 'true') {
    appendParam(params, 'deletedOnly', 'true')
  }
  appendParam(params, 'paymentType', query.paymentType)
  appendParam(params, 'action', query.action)
  appendParam(params, 'entityType', query.entityType)
  return params
}

export function withQuery(url: string, params: URLSearchParams): string {
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

export function accountingScopeQueryKey(query: AccountingScopeQuery = {}): string {
  const scope = query.scope === 'ESTABLISHMENT' ? 'ESTABLISHMENT' : 'GROUP'
  return [
    scope,
    scope === 'ESTABLISHMENT' ? query.establishmentId || '' : '',
    query.academicYear || '',
    query.from || '',
    query.to || '',
    query.paymentMethod || '',
  ].join('|')
}

export function accountingListQueryKey(query: AccountingListQuery = {}): string {
  return [
    accountingScopeQueryKey(query),
    String(query.page || 1),
    String(query.limit || ''),
    query.search || '',
    query.ticketNumber || '',
    query.sort || '',
    query.sortBy || '',
    query.sortOrder || '',
    query.status || '',
    query.category || '',
    query.documentType || '',
    query.invoiceStatus || '',
    query.missingInvoice === true || query.missingInvoice === 'true' ? '1' : '',
    query.withholdingReviewStatus || '',
    query.tejStatus || '',
    query.supplierName || '',
    query.supplierInvoiceNumber || '',
    query.deliveryNoteNumber || '',
    query.internalReference || '',
    String(query.minAmountTtc || ''),
    String(query.maxAmountTtc || ''),
    query.visibility || '',
    query.excludeStatus || '',
    query.excludeExpenseStatus || '',
    query.includeCancelledExpenses === true ? '1' : query.includeCancelledExpenses === false ? '0' : '',
    query.includeDeleted === true || query.includeDeleted === 'true'
      ? '1'
      : query.includeDeleted === false || query.includeDeleted === 'false'
        ? '0'
        : '',
    query.deletedOnly === true || query.deletedOnly === 'true' ? '1' : '',
    query.action || '',
    query.entityType || '',
  ].join('|')
}

export const ACCOUNTING_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
export const ACCOUNTING_ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
export const ACCOUNTING_SEARCH_DEBOUNCE_MS = 350
export const ACCOUNTING_FOLLOWUPS_SEARCH_DEBOUNCE_MS = 300
export const ACCOUNTING_FOLLOWUP_DEFAULT_LIMIT = 20
export const ACCOUNTING_FOLLOWUP_DEFAULT_SORT_BY: AccountingFollowUpSortField = 'nextFollowUpAt'

export function normalizeInvoiceFollowUpLimit(limit?: number): number {
  return ACCOUNTING_FOLLOWUP_PAGE_SIZES.includes(limit as (typeof ACCOUNTING_FOLLOWUP_PAGE_SIZES)[number])
    ? Number(limit)
    : ACCOUNTING_FOLLOWUP_DEFAULT_LIMIT
}

export function normalizeInvoiceFollowUpSearch(search?: string | null): string | undefined {
  const value = String(search || '').trim().slice(0, 80)
  return value || undefined
}

export function normalizeInvoiceFollowUpSortBy(sortBy?: string | null): AccountingFollowUpSortField {
  return ACCOUNTING_FOLLOWUP_SORT_FIELDS.includes(sortBy as AccountingFollowUpSortField)
    ? (sortBy as AccountingFollowUpSortField)
    : ACCOUNTING_FOLLOWUP_DEFAULT_SORT_BY
}

export function applyInvoiceFollowUpCancelledExpenseVisibility(
  query: AccountingListQuery,
  includeCancelledExpenses = false
): AccountingListQuery {
  const next: AccountingListQuery = {
    ...query,
    includeCancelledExpenses: Boolean(includeCancelledExpenses),
  }
  if (includeCancelledExpenses) {
    delete next.excludeExpenseStatus
    return next
  }
  next.excludeExpenseStatus = 'CANCELLED'
  return next
}

export function followUpLinkedExpenseStatus(row?: {
  status?: string | null
  expenseStatus?: string | null
  expense?: {status?: string | null} | null
} | null): string {
  return String(row?.expenseStatus || row?.expense?.status || '').trim().toUpperCase()
}

export function isFollowUpLinkedExpenseCancelled(row?: {
  status?: string | null
  expenseStatus?: string | null
  expense?: {status?: string | null} | null
} | null): boolean {
  return followUpLinkedExpenseStatus(row) === 'CANCELLED'
}

export function filterInvoiceFollowUpRowsForCancelledVisibility<T extends {
  expenseStatus?: string | null
  expense?: {status?: string | null} | null
}>(items: T[], includeCancelledExpenses = false): T[] {
  if (includeCancelledExpenses) return items
  return items.filter((row) => !isFollowUpLinkedExpenseCancelled(row))
}

export function buildInvoiceFollowUpListQuery(
  query: AccountingScopeQuery,
  options: {
    page?: number
    limit?: number
    search?: string
    status?: string
    sortBy?: string
    sortOrder?: string
    includeCancelledExpenses?: boolean
  } = {}
): AccountingListQuery {
  const status = String(options.status || '').toUpperCase()
  return applyInvoiceFollowUpCancelledExpenseVisibility(
    {
      ...query,
      page: Math.max(1, Number(options.page) || 1),
      limit: normalizeInvoiceFollowUpLimit(options.limit),
      search: normalizeInvoiceFollowUpSearch(options.search),
      sortBy: normalizeInvoiceFollowUpSortBy(options.sortBy),
      sortOrder: options.sortOrder === 'desc' ? 'desc' : 'asc',
      status: !status || status === 'ALL' ? undefined : status,
    },
    Boolean(options.includeCancelledExpenses)
  )
}

function finiteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

export function parseInvoiceFollowUpStats(
  stats: unknown
): {available: boolean; stats: AccountingInvoiceFollowUpStats | null} {
  if (!stats || typeof stats !== 'object') return {available: false, stats: null}
  const source = stats as Record<string, unknown>
  const parsed: AccountingInvoiceFollowUpStats = {}
  ;(['missingInvoices', 'overdue', 'contacted', 'resolved'] as const).forEach((key) => {
    const value = finiteNumber(source[key])
    if (value !== undefined) parsed[key] = value
  })
  return Object.keys(parsed).length ? {available: true, stats: parsed} : {available: false, stats: null}
}

export function selectInvoiceFollowUpRows(payload: unknown): AccountingInvoiceFollowUp[] {
  const data = (payload || {}) as {items?: unknown; followUps?: unknown; invoiceFollowUps?: unknown}
  const rows = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.followUps)
      ? data.followUps
      : Array.isArray(data.invoiceFollowUps)
        ? data.invoiceFollowUps
        : []
  return rows.map((row) => {
    const rec = asObjectRecord(row) || {}
    const expense = asObjectRecord(rec.expense)
    const expenseStatus = String(rec.expenseStatus || expense?.status || '').trim() || undefined
    return {
      ...(rec as unknown as AccountingInvoiceFollowUp),
      id: String(rec.id ?? rec._id ?? ''),
      expenseStatus,
      expense: expense
        ? ({...expense, status: expense.status || expenseStatus} as AccountingInvoiceFollowUp['expense'])
        : expenseStatus
          ? ({status: expenseStatus} as AccountingInvoiceFollowUp['expense'])
          : (rec.expense as AccountingInvoiceFollowUp['expense']),
    }
  })
}

export function parseInvoiceFollowUpListResponse(payload: unknown): {
  items: AccountingInvoiceFollowUp[]
  paginated: boolean
  page: number
  limit: number
  total: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  stats: AccountingInvoiceFollowUpStats | null
  statsAvailable: boolean
} {
  const data = (payload || {}) as {
    pagination?: Partial<AccountingInvoiceFollowUpPagination> & {pages?: unknown}
    total?: unknown
    stats?: unknown
  }
  const items = selectInvoiceFollowUpRows(data)
  const pagination = data.pagination
  const paginated = Boolean(pagination && typeof pagination === 'object' && pagination.page != null)
  const statsParsed = parseInvoiceFollowUpStats(data.stats)
  if (!paginated) {
    const total = finiteNumber(data.total)
    return {
      items,
      paginated: false,
      page: 1,
      limit: items.length || ACCOUNTING_FOLLOWUP_DEFAULT_LIMIT,
      total: total !== undefined ? total : items.length,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false,
      stats: statsParsed.stats,
      statsAvailable: statsParsed.available,
    }
  }
  const page = Math.max(1, finiteNumber(pagination?.page) || 1)
  const limit = normalizeInvoiceFollowUpLimit(finiteNumber(pagination?.limit))
  const total = finiteNumber(pagination?.total)
  const totalPages = finiteNumber(pagination?.totalPages) ?? finiteNumber(pagination?.pages)
  return {
    items,
    paginated: true,
    page,
    limit,
    total: total !== undefined ? total : 0,
    totalPages: totalPages !== undefined ? totalPages : 0,
    hasNextPage: Boolean(pagination?.hasNextPage),
    hasPreviousPage: Boolean(pagination?.hasPreviousPage),
    stats: statsParsed.stats,
    statsAvailable: statsParsed.available,
  }
}

export function isAccountingAttachmentArchived(row: {
  archived?: boolean
  isArchived?: boolean
  isActive?: boolean
}): boolean {
  if (row.archived === true || row.isArchived === true) return true
  if (row.isActive === false) return true
  return false
}

export function accountingAttachmentId(row: {id?: string; _id?: string}): string {
  return String(row.id || row._id || '')
}

export function safeAccountingDownloadName(name?: string | null): string {
  const cleaned = String(name || 'document')
    .replace(/[/\\?%*:|"<>]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180)
  return cleaned || 'document'
}

export function parseAccountingContentDisposition(header?: string | null, fallback?: string): string {
  if (!header) return safeAccountingDownloadName(fallback)
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf?.[1]) {
    try {
      return safeAccountingDownloadName(decodeURIComponent(utf[1].replace(/"/g, '')))
    } catch {
      return safeAccountingDownloadName(utf[1])
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return safeAccountingDownloadName(plain?.[1] || fallback)
}

export function triggerAccountingBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = safeAccountingDownloadName(fileName)
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export function accountingAttachmentPreviewMode(
  blob: Blob | null | undefined,
  fileName?: string | null
): 'image' | 'pdf' | 'unsupported' {
  const type = String(blob?.type || '').toLowerCase()
  const name = String(fileName || '').toLowerCase()
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(name)) return 'image'
  if (type.includes('pdf') || name.endsWith('.pdf')) return 'pdf'
  return 'unsupported'
}

export function validateAccountingAttachmentFile(
  file?: File | null,
  kind?: string
): string | null {
  if (!file) return 'FILE_REQUIRED'
  if (file.size > ACCOUNTING_ATTACHMENT_MAX_BYTES) return 'FILE_TOO_LARGE'
  const name = String(file.name || '').toLowerCase()
  const type = String(file.type || '').toLowerCase()
  const allowedType =
    type === 'application/pdf' ||
    type === 'image/jpeg' ||
    type === 'image/jpg' ||
    type === 'image/png' ||
    name.endsWith('.pdf') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png')
  if (!allowedType) return 'FILE_TYPE_INVALID'
  const allowedKinds = ['INVOICE', 'BL', 'TEJ', 'OTHER']
  if (kind && !allowedKinds.includes(String(kind).toUpperCase())) return 'KIND_INVALID'
  return null
}

export function formatAccountingTndOrDash(value: number | string | null | undefined): string {
  const numeric = accountingMetricValue(value)
  if (numeric === undefined) return '—'
  return formatAccountingTnd(numeric)
}

/** Phase 1A sends amountTtc in TND and amountTtcMills in millimes. Never invent 0. */
export function followUpAmountTnd(row: {
  amountTtc?: number | string | null
  amountTtcMills?: number | string | null
  totalTtc?: number | string | null
  expense?: {totalTtc?: number | string | null; totalTtcMills?: number | string | null} | null
  expenseMissing?: boolean
}): number | undefined {
  if (row.expenseMissing) return undefined
  const mills = accountingMetricValue(row.amountTtcMills ?? row.expense?.totalTtcMills)
  const ttc = accountingMetricValue(row.amountTtc ?? row.totalTtc ?? row.expense?.totalTtc)
  if (mills != null) {
    const fromMills = mills / 1000
    if (ttc != null) {
      if (Math.abs(ttc - fromMills) <= 0.001) return ttc
      if (Math.abs(ttc - mills) <= 0.001) return fromMills
    }
    return fromMills
  }
  if (ttc == null) return undefined
  if (Number.isInteger(ttc) && Math.abs(ttc) >= 10000) return ttc / 1000
  return ttc
}

export type InvoiceReceiptConfirmation =
  | 'VERIFIED'
  | 'PENDING_VERIFICATION'
  | 'MISSING_EXPENSE'
  | 'INCOMPLETE'

export function evaluateInvoiceReceiptConfirmation(input: {
  invoiceStatus?: string | null
  followUpStatus?: string | null
  followUpIsActive?: boolean | null
  expenseMissing?: boolean
}): InvoiceReceiptConfirmation {
  if (input.expenseMissing) return 'MISSING_EXPENSE'
  const invoiceStatus = String(input.invoiceStatus || '').toUpperCase()
  const followUpStatus = String(input.followUpStatus || '').toUpperCase()
  const followUpResolved = followUpStatus === 'RESOLVED' || input.followUpIsActive === false
  if (invoiceStatus === 'RECEIVED' && followUpResolved) return 'VERIFIED'
  if (invoiceStatus === 'PENDING_VERIFICATION') return 'PENDING_VERIFICATION'
  return 'INCOMPLETE'
}

export function thunkHasError(result: unknown): boolean {
  return Boolean(result && typeof result === 'object' && (result as {error?: unknown}).error)
}

export function thunkPayload<T = any>(result: unknown): T | undefined {
  if (!result || typeof result !== 'object') return undefined
  return (result as {payload?: T}).payload
}

export async function completeSupplierInvoiceReceipt(input: {
  expenseId: string
  supplierInvoiceNumber: string
  supplierInvoiceDate?: string
  supplierName?: string
  supplierTaxId?: string
  file: File | null
  alreadyUploaded: boolean
  attachInvoice: (id: string, body: AccountingAttachInvoicePayload) => Promise<unknown>
  uploadAttachment: (id: string, file: File, kind: 'INVOICE') => Promise<unknown>
}): Promise<{ok: true; alreadyUploaded: true} | {ok: false; step: 'validation' | 'upload' | 'metadata'; alreadyUploaded: boolean; error?: string}> {
  const number = String(input.supplierInvoiceNumber || '').trim()
  if (!number) {
    return {ok: false, step: 'validation', alreadyUploaded: input.alreadyUploaded}
  }
  let alreadyUploaded = input.alreadyUploaded
  if (!alreadyUploaded) {
    const fileError = validateAccountingAttachmentFile(input.file, 'INVOICE')
    if (fileError) {
      return {ok: false, step: 'validation', alreadyUploaded: false, error: fileError}
    }
    const upload = await input.uploadAttachment(input.expenseId, input.file as File, 'INVOICE')
    if (thunkHasError(upload)) {
      return {
        ok: false,
        step: 'upload',
        alreadyUploaded: false,
        error: thunkPayload<{error?: string}>(upload)?.error,
      }
    }
    alreadyUploaded = true
  }
  const metadata = await input.attachInvoice(input.expenseId, {
    supplierInvoiceNumber: number,
    supplierInvoiceDate: input.supplierInvoiceDate || undefined,
    supplierName: input.supplierName,
    supplierTaxId: input.supplierTaxId,
  })
  if (thunkHasError(metadata)) {
    return {
      ok: false,
      step: 'metadata',
      alreadyUploaded,
      error: thunkPayload<{error?: string}>(metadata)?.error,
    }
  }
  return {ok: true, alreadyUploaded: true}
}

export function previewExpenseTtc(amountHt: unknown, vatAmount: unknown): number {
  const ht = Math.round(Number(amountHt || 0) * 1000)
  const vat = Math.round(Number(vatAmount || 0) * 1000)
  if (!Number.isFinite(ht) || !Number.isFinite(vat)) return 0
  return (ht + vat) / 1000
}

export function expenseInvoiceOrBlNumber(row: {
  supplierInvoiceNumber?: string
  deliveryNoteNumber?: string
  invoiceNumber?: string
}): string {
  return row.supplierInvoiceNumber || row.deliveryNoteNumber || row.invoiceNumber || ''
}

export function needsWithholdingReview(reviewStatus?: string | null): boolean {
  return String(reviewStatus || '').toUpperCase() === 'NEEDS_REVIEW'
}

export const ACCOUNTING_EMAIL_REMINDER_MAX_CC = 5
export const ACCOUNTING_EMAIL_REMINDER_PREVIEW_NOTICE_FR =
  "Cette relance est un brouillon. Aucun e-mail n'a encore été envoyé."

export function isValidAccountingSupplierEmail(value?: string | null): boolean {
  const email = String(value || '').trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function normalizeSupplierEmailAvailability(
  value?: string | null
): AccountingSupplierEmailAvailability | '' {
  const raw = String(value || '').trim().toUpperCase()
  if (raw === 'AVAILABLE' || raw === 'NOT_AVAILABLE' || raw === 'UNKNOWN') return raw
  return ''
}

export function validateSupplierExpenseForm(input: {
  establishmentId?: string
  expenseDate?: string
  documentType?: string
  supplierInvoiceNumber?: string
  deliveryNoteNumber?: string
  description?: string
  missingInvoiceJustification?: string
  requireEmailChoice?: boolean
  supplierEmailAvailability?: string | null
  supplierEmail?: string | null
}): string | null {
  if (!input.establishmentId) return 'ESTABLISHMENT_REQUIRED'
  if (!isCompleteAccountingDate(input.expenseDate)) return 'DATE_REQUIRED'
  if (input.requireEmailChoice) {
    const choice = normalizeSupplierEmailAvailability(input.supplierEmailAvailability)
    if (choice !== 'AVAILABLE' && choice !== 'NOT_AVAILABLE') return 'EMAIL_AVAILABILITY_REQUIRED'
  }
  if (normalizeSupplierEmailAvailability(input.supplierEmailAvailability) === 'AVAILABLE') {
    if (!String(input.supplierEmail || '').trim()) return 'SUPPLIER_EMAIL_REQUIRED'
    if (!isValidAccountingSupplierEmail(input.supplierEmail)) return 'SUPPLIER_EMAIL_INVALID'
  }
  const type = String(input.documentType || 'FACTURE').toUpperCase()
  if (type === 'FACTURE' && !String(input.supplierInvoiceNumber || '').trim()) {
    return 'INVOICE_NUMBER_REQUIRED'
  }
  if (type === 'BL' && !String(input.deliveryNoteNumber || '').trim()) {
    return 'BL_NUMBER_REQUIRED'
  }
  if (
    type === 'OTHER' &&
    !String(input.description || '').trim() &&
    !String(input.missingInvoiceJustification || '').trim() &&
    !String(input.supplierInvoiceNumber || '').trim()
  ) {
    return 'OTHER_JUSTIFICATION_REQUIRED'
  }
  return null
}

export function shouldApplyAccountingSupplierLookup(input: {
  requestSeq: number
  currentSeq: number
  requestName: string
  currentName: string
  requestEstablishmentId: string
  currentEstablishmentId: string
}): boolean {
  return (
    input.requestSeq === input.currentSeq &&
    input.requestName.trim().toLowerCase() === input.currentName.trim().toLowerCase() &&
    String(input.requestEstablishmentId || '') === String(input.currentEstablishmentId || '')
  )
}

function asObjectRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function parseAccountingSupplierLookup(payload: unknown): AccountingSupplierLookupItem[] {
  const root = asObjectRecord(payload) || {}
  const nested = asObjectRecord(root.data) || asObjectRecord(root.payload) || root
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(nested.suppliers)
      ? nested.suppliers
      : Array.isArray(nested.items)
        ? nested.items
        : Array.isArray(nested.results)
          ? nested.results
          : nested.supplier
            ? [nested.supplier]
            : []
  return raw
    .map((row) => {
      const rec = asObjectRecord(row) || {}
      const name = String(rec.supplierName || rec.name || '').trim()
      const email = String(rec.supplierEmail || rec.email || '').trim()
      const availability =
        normalizeSupplierEmailAvailability(String(rec.supplierEmailAvailability || rec.emailAvailability || '')) ||
        (email ? 'AVAILABLE' : '')
      return {
        id: rec.id != null ? String(rec.id) : rec._id != null ? String(rec._id) : undefined,
        supplierName: name || undefined,
        supplierTaxId: rec.supplierTaxId != null ? String(rec.supplierTaxId) : rec.taxId != null ? String(rec.taxId) : undefined,
        supplierEmail: email || undefined,
        supplierEmailAvailability: availability || undefined,
      }
    })
    .filter((row) => row.supplierName || row.supplierEmail)
}

export function parseAccountingEmailCcInput(value: unknown): string[] {
  const parts = Array.isArray(value)
    ? value.flatMap((item) => String(item || '').split(/[,;]/))
    : String(value || '').split(/[,;]/)
  const seen = new Set<string>()
  const emails: string[] = []
  for (const part of parts) {
    const email = part.trim()
    if (!email) continue
    const key = email.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    emails.push(email)
  }
  return emails
}

export function validateAccountingEmailReminderCc(input: {
  cc: string[]
  recipient?: string | null
}): 'CC_INVALID' | 'CC_TOO_MANY' | 'CC_TO_EXCLUDED' | null {
  if (input.cc.length > ACCOUNTING_EMAIL_REMINDER_MAX_CC) return 'CC_TOO_MANY'
  const to = String(input.recipient || '').trim().toLowerCase()
  for (const email of input.cc) {
    if (!isValidAccountingSupplierEmail(email)) return 'CC_INVALID'
    if (to && email.toLowerCase() === to) return 'CC_TO_EXCLUDED'
  }
  return null
}

export function stripAccountingEmailReminderPreviewNotice(body: string, notice?: string | null): string {
  let next = String(body || '')
  const notices = [notice, ACCOUNTING_EMAIL_REMINDER_PREVIEW_NOTICE_FR].filter(Boolean) as string[]
  for (const item of notices) {
    if (!item) continue
    next = next.split(item).join('')
  }
  return next.replace(/^\s+|\s+$/g, '')
}

export function splitAccountingEmailReminderSignature(
  body: string,
  signatureLines?: string[] | null
): {message: string; signatureLines: string[]} {
  const lines = (signatureLines || []).map((line) => String(line || '').trimEnd()).filter((line) => line !== '')
  let message = String(body || '').replace(/\s+$/g, '')
  if (lines.length) {
    const block = lines.join('\n')
    if (message.endsWith(block)) {
      message = message.slice(0, message.length - block.length).replace(/\s+$/g, '')
    }
  }
  return {message, signatureLines: lines}
}

export function composeAccountingEmailReminderBody(message: string, signatureLines?: string[] | null): string {
  const text = String(message || '').replace(/\s+$/g, '')
  const lines = (signatureLines || []).map((line) => String(line || '').trimEnd()).filter((line) => line !== '')
  const signature = lines.join('\n')
  if (!signature) return text
  if (text.includes(signature)) return text
  return text ? `${text}\n\n${signature}` : signature
}

export function parseAccountingEmailReminderEstablishment(value: unknown): AccountingEmailReminderDraft['establishment'] {
  const rec = asObjectRecord(value)
  if (!rec) return undefined
  const fiscalName = String(rec.fiscalName || rec.legalName || rec.name || '').trim() || undefined
  const taxId = String(rec.taxId || rec.matriculeFiscal || rec.mf || '').trim() || undefined
  const phone = String(rec.phone || rec.telephone || rec.tel || '').trim() || undefined
  const name = String(rec.name || '').trim() || undefined
  if (!fiscalName && !taxId && !phone && !name) return undefined
  return {name, fiscalName, taxId, phone}
}

function firstAccountingEmailAddress(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (value == null || value === '') continue
    if (typeof value === 'string' || typeof value === 'number') {
      const email = String(value).trim()
      if (email) return email
      continue
    }
    const rec = asObjectRecord(value)
    if (!rec) continue
    const nested = firstAccountingEmailAddress(
      rec.recipient,
      rec.to,
      rec.email,
      rec.supplierEmail,
      rec.supplier
    )
    if (nested) return nested
  }
  return undefined
}

export function followUpSupplierEmail(row?: {
  supplierEmail?: string | null
  expense?: {supplierEmail?: string | null} | null
} | null): string {
  return String(row?.supplierEmail || row?.expense?.supplierEmail || '').trim()
}

export function parseAccountingEmailReminderDraft(payload: unknown): AccountingEmailReminderDraft {
  const root = asObjectRecord(payload) || {}
  const nested =
    asObjectRecord(root.draft) ||
    asObjectRecord(root.reminder) ||
    asObjectRecord(root.email) ||
    asObjectRecord(root.data) ||
    asObjectRecord(root.payload) ||
    root
  const previewNotice =
    String(nested.previewNotice || nested.notice || '').trim() || ACCOUNTING_EMAIL_REMINDER_PREVIEW_NOTICE_FR
  const signatureLines = Array.isArray(nested.signatureLines)
    ? nested.signatureLines.map((line) => String(line || '')).filter((line) => line.trim())
    : []
  const rawBody = String(nested.body || nested.message || nested.messageBody || nested.text || '')
  const withoutNotice = stripAccountingEmailReminderPreviewNotice(rawBody, previewNotice)
  return {
    recipient:
      firstAccountingEmailAddress(
        nested.recipient,
        nested.to,
        nested.email,
        nested.supplierEmail,
        nested.supplier,
        root.recipient,
        root.to,
        root.email,
        root.supplierEmail,
        root.supplier
      ) || undefined,
    cc: parseAccountingEmailCcInput(nested.cc),
    subject: String(nested.subject || '').trim() || undefined,
    body: withoutNotice,
    previewNotice,
    signatureLines,
    establishment: parseAccountingEmailReminderEstablishment(nested.establishment),
    deliveryNoteNumber:
      String(nested.deliveryNoteNumber || nested.blNumber || '').trim() || undefined,
    expenseReference:
      String(nested.expenseReference || nested.reference || nested.expenseRef || '').trim() || undefined,
  }
}

export function normalizeAccountingEmailReminderStatus(value?: string | null, channel?: string | null): string {
  const raw = String(value || '').trim().toUpperCase()
  const ch = String(channel || '').trim().toUpperCase()
  if (raw === 'QUEUED' || raw === 'SENT' || raw === 'FAILED' || raw === 'MANUAL_CONTACT') return raw
  if (raw === 'SEND_IN_PROGRESS' || raw === 'IN_PROGRESS' || raw === 'SENDING') return 'SEND_IN_PROGRESS'
  if (ch === 'PHONE' || ch === 'OTHER') {
    if (!raw || raw === 'RECORDED' || raw === 'MANUAL') return 'MANUAL_CONTACT'
  }
  return raw
}

export function isAccountingEmailReminderOutcomeEstablished(status?: string | null): boolean {
  const normalized = normalizeAccountingEmailReminderStatus(status)
  return normalized === 'SENT' || normalized === 'FAILED' || normalized === 'QUEUED' || normalized === 'MANUAL_CONTACT'
}

export function isAccountingEmailReminderAttemptOpen(status?: string | null): boolean {
  const normalized = String(status || '')
  return normalized === 'SEND_IN_PROGRESS' || normalized === 'UNRESOLVED'
}

function accountingFlagEnabled(value: unknown): boolean {
  return value === true || value === 'true' || value === 1
}

export function accountingEmailReminderErrorMessageId(errorCode?: string, status?: number): string {
  const code = String(errorCode || '')
  if (code === 'IDEMPOTENCY_KEY_REQUIRED') return 'ACCOUNTING.FOLLOWUPS.ERR_IDEMPOTENCY_REQUIRED'
  if (code === 'NOT_FOUND') return 'ACCOUNTING.FOLLOWUPS.ERR_STATUS_NOT_FOUND'
  if (code === 'SEND_IN_PROGRESS') return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_IN_PROGRESS'
  if (code === 'IDEMPOTENCY_KEY_CONFLICT') return 'ACCOUNTING.FOLLOWUPS.ERR_IDEMPOTENCY_CONFLICT'
  if (code === 'SUPPLIER_EMAIL_NOT_AVAILABLE') return 'ACCOUNTING.FOLLOWUPS.EMAIL_UNAVAILABLE'
  if (code === 'FOLLOWUP_RESOLVED') return 'ACCOUNTING.FOLLOWUPS.REMINDER_LOCKED'
  if (code === 'EXPENSE_CANCELLED') return 'ACCOUNTING.FOLLOWUPS.REMINDER_LOCKED'
  if (code === 'EMAIL_REMINDER_RATE_LIMITED') return 'ACCOUNTING.FOLLOWUPS.ERR_RATE_LIMITED'
  if (code === 'EMAIL_SEND_FAILED') return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_FAILED'
  if (code === 'CC_INVALID' || code === 'INVALID_CC') return 'ACCOUNTING.FOLLOWUPS.ERR_CC_INVALID'
  if (code === 'CC_TOO_MANY') return 'ACCOUNTING.FOLLOWUPS.ERR_CC_TOO_MANY'
  if (code === 'CC_TO_EXCLUDED') return 'ACCOUNTING.FOLLOWUPS.ERR_CC_TO_EXCLUDED'
  if (code === 'TO_INVALID' || code === 'INVALID_RECIPIENT' || code === 'INVALID_TO') {
    return 'ACCOUNTING.FOLLOWUPS.ERR_TO_INVALID'
  }
  if (code === 'TO_REQUIRED' || code === 'RECIPIENT_REQUIRED') return 'ACCOUNTING.FOLLOWUPS.ERR_TO_REQUIRED'
  if (status === 404) return 'ACCOUNTING.FOLLOWUPS.ERR_STATUS_NOT_FOUND'
  if (status === 429) return 'ACCOUNTING.FOLLOWUPS.ERR_RATE_LIMITED'
  if (status === 502) return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_FAILED'
  return 'ACCOUNTING.ERROR'
}

export function isAccountingRequestTimeout(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const rec = error as Record<string, unknown>
  const payload =
    rec.payload && typeof rec.payload === 'object' ? (rec.payload as Record<string, unknown>) : rec
  const code = String(payload.errorCode || rec.code || rec.errorCode || '')
  const message = String(payload.error || rec.error || rec.message || '')
  const status = payload.status ?? rec.status
  if (Number(status) === 502 || code === 'EMAIL_SEND_FAILED') return false
  if (code === 'REQUEST_TIMEOUT' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true
  if (status === 408) return true
  return /timeout/i.test(message)
}

export function accountingEmailReminderStatusMessageId(status?: string | null, channel?: string | null): string {
  switch (normalizeAccountingEmailReminderStatus(status, channel)) {
    case 'QUEUED':
      return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_QUEUED'
    case 'SENT':
      return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_SENT'
    case 'FAILED':
      return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_FAILED'
    case 'SEND_IN_PROGRESS':
      return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_IN_PROGRESS'
    case 'MANUAL_CONTACT':
      return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_MANUAL'
    case 'UNRESOLVED':
      return 'ACCOUNTING.FOLLOWUPS.SEND_STATUS_UNRESOLVED'
    default:
      return ''
  }
}

export function parseAccountingEmailReminderSendResult(payload: unknown): AccountingEmailReminderSendResult {
  const root = asObjectRecord(payload) || {}
  const nested = asObjectRecord(root.data) || asObjectRecord(root.payload) || root
  const reminder = asObjectRecord(nested.reminder)
  const reminderStatus = reminder
    ? normalizeAccountingEmailReminderStatus(String(reminder.status || ''))
    : ''
  const sent = accountingFlagEnabled(nested.sent)
  const queued = accountingFlagEnabled(nested.queued)
  const contacted = accountingFlagEnabled(nested.contacted)
  const inProgress = accountingFlagEnabled(nested.inProgress)
  const duplicate = accountingFlagEnabled(nested.duplicate)
  let status = reminderStatus
  if (!status) {
    if (inProgress) status = 'SEND_IN_PROGRESS'
    else if (sent) status = 'SENT'
    else if (queued) status = 'QUEUED'
  }
  return {
    status: status || undefined,
    sent,
    queued,
    contacted,
    inProgress,
    duplicate,
    recipient:
      String(reminder?.recipient || nested.recipient || nested.to || '').trim() || undefined,
    subject: String(reminder?.subject || nested.subject || '').trim() || undefined,
    body: String(reminder?.body || reminder?.message || nested.body || nested.message || '').trim() || undefined,
    reminderId:
      reminder?.id != null
        ? String(reminder.id)
        : nested.reminderId != null
          ? String(nested.reminderId)
          : undefined,
  }
}

export function parseAccountingEmailReminderSendResultFromThunk(result: unknown): AccountingEmailReminderSendResult {
  const payload = thunkPayload(result) ?? result
  const rec = asObjectRecord(payload) || {}
  if (rec.data != null) return parseAccountingEmailReminderSendResult(rec.data)
  return parseAccountingEmailReminderSendResult(payload)
}

export function parseAccountingFollowUpReminders(payload: unknown): AccountingFollowUpReminder[] {
  const root = asObjectRecord(payload) || {}
  const nested = asObjectRecord(root.data) || asObjectRecord(root.payload) || root
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(nested.reminders)
      ? nested.reminders
      : Array.isArray(nested.items)
        ? nested.items
        : Array.isArray(nested.history)
          ? nested.history
          : []
  const rows = raw.map((row, index) => {
    const rec = asObjectRecord(row) || {}
    const actorRaw = rec.actor || rec.user || rec.createdBy || rec.createdByUser
    const actor =
      typeof actorRaw === 'string'
        ? actorRaw
        : auditActorLabel(actorRaw as {firstName?: string; lastName?: string; email?: string} | string | null)
    return {
      id: rec.id != null ? String(rec.id) : rec._id != null ? String(rec._id) : String(index),
      createdAt: String(rec.createdAt || rec.contactedAt || rec.occurredAt || rec.sentAt || rec.date || ''),
      channel: rec.channel != null ? String(rec.channel) : undefined,
      recipient: String(rec.recipient || rec.email || rec.to || '').trim() || undefined,
      status: normalizeAccountingEmailReminderStatus(
        String(rec.status || rec.eventStatus || rec.state || ''),
        rec.channel != null ? String(rec.channel) : undefined
      ) || undefined,
      actor: actor || undefined,
      note: String(rec.note || rec.notes || rec.comment || '').trim() || undefined,
      subject: String(rec.subject || '').trim() || undefined,
      body: String(rec.body || rec.message || rec.messageBody || '').trim() || undefined,
      cc: parseAccountingEmailCcInput(rec.cc),
      nextFollowUpAt: String(rec.nextFollowUpAt || rec.nextFollowUpDate || '').trim() || undefined,
    }
  })
  return rows.sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt)))
}

export function isSupplierEmailAvailableForReminder(row?: {
  supplierEmail?: string | null
  supplierEmailAvailability?: string | null
  expense?: {
    supplierEmail?: string | null
    supplierEmailAvailability?: string | null
    status?: string | null
  } | null
} | null): boolean {
  const availability = normalizeSupplierEmailAvailability(
    row?.supplierEmailAvailability || row?.expense?.supplierEmailAvailability
  )
  if (availability === 'NOT_AVAILABLE' || availability === 'UNKNOWN') return false
  const email = row?.supplierEmail || row?.expense?.supplierEmail
  if (availability === 'AVAILABLE') return isValidAccountingSupplierEmail(email) || Boolean(String(email || '').trim())
  return isValidAccountingSupplierEmail(email)
}

export function isAccountingFollowUpReminderLocked(row?: {
  status?: string | null
  isActive?: boolean | null
  expenseStatus?: string | null
  expense?: {status?: string | null} | null
} | null): boolean {
  const followStatus = String(row?.status || '').toUpperCase()
  if (followStatus === 'RESOLVED' || row?.isActive === false) return true
  if (isFollowUpLinkedExpenseCancelled(row)) return true
  return false
}

export function followUpEmailReminderBlockReason(row?: {
  status?: string | null
  isActive?: boolean | null
  invoiceStatus?: string | null
  supplierEmail?: string | null
  supplierEmailAvailability?: string | null
  expenseStatus?: string | null
  expense?: {
    status?: string | null
    supplierEmail?: string | null
    supplierEmailAvailability?: string | null
    invoiceStatus?: string | null
  } | null
} | null): 'LOCKED' | 'RECEIVED' | 'NO_EMAIL' | null {
  if (!row) return null
  const invoiceStatus = String(row.invoiceStatus || row.expense?.invoiceStatus || '').toUpperCase()
  if (invoiceStatus === 'RECEIVED') return 'RECEIVED'
  if (isAccountingFollowUpReminderLocked(row)) return 'LOCKED'
  const availability = normalizeSupplierEmailAvailability(
    row.supplierEmailAvailability || row.expense?.supplierEmailAvailability
  )
  if (availability === 'NOT_AVAILABLE' || availability === 'UNKNOWN') return 'NO_EMAIL'
  if (availability === 'AVAILABLE' && !isSupplierEmailAvailableForReminder(row)) return 'NO_EMAIL'
  return null
}

export function matchesExpenseListFilters(
  row: {
    documentType?: string | null
    invoiceStatus?: string | null
    tej?: {status?: string | null}
    withholding?: {reviewStatus?: string | null}
  },
  filters: {documentType?: string; invoiceStatus?: string; tejStatus?: string; withholdingReview?: string}
): boolean {
  if (filters.documentType && String(row.documentType || '') !== filters.documentType) return false
  if (filters.invoiceStatus && String(row.invoiceStatus || '') !== filters.invoiceStatus) return false
  if (filters.tejStatus && String(row.tej?.status || '') !== filters.tejStatus) return false
  if (
    filters.withholdingReview &&
    String(row.withholding?.reviewStatus || '') !== filters.withholdingReview
  ) {
    return false
  }
  return true
}

const inflightGets = new Map<string, Promise<unknown>>()

export function dedupeInflightGet<T>(key: string, factory: () => Promise<T>): Promise<T> {
  const existing = inflightGets.get(key)
  if (existing) return existing as Promise<T>
  const created = factory().finally(() => {
    if (inflightGets.get(key) === created) inflightGets.delete(key)
  })
  inflightGets.set(key, created)
  return created
}

export function resetAccountingInflightForTests(): void {
  inflightGets.clear()
}

export function shouldFetchAccounting(
  previousKey: string | null,
  nextKey: string,
  force = false
): boolean {
  if (force) return true
  return previousKey !== nextKey
}

export function isDraftExpense(status?: string | null): boolean {
  return String(status || '').toUpperCase() === 'DRAFT'
}

export function canPayExpense(status?: string | null): boolean {
  const s = String(status || '').toUpperCase()
  return s === 'VALIDATED' || s === 'PARTIALLY_PAID'
}

export function canCancelExpense(status?: string | null): boolean {
  const s = String(status || '').toUpperCase()
  return s !== 'CANCELLED' && Boolean(s)
}

export function needsReversalConfirm(status?: string | null): boolean {
  const s = String(status || '').toUpperCase()
  return s === 'PAID' || s === 'PARTIALLY_PAID'
}

export function payerName(row: {
  guestPayerName?: string | null
  user?: {firstName?: string; lastName?: string} | null
  isRegisteredUser?: boolean
}): string {
  const first = row.user?.firstName || ''
  const last = row.user?.lastName || ''
  const registered = `${first} ${last}`.trim()
  if (registered) return registered
  return (row.guestPayerName || '').trim() || '—'
}

export function isSupportedReportFormat(format: string): format is AccountingReportFormat {
  return format === 'json' || format === 'csv' || format === 'html' || format === 'pdf'
}

export function accountingIsExpenseOnlyReport(type?: string): boolean {
  return type === 'expense-journal'
}

export function accountingReportAcceptHeader(format: AccountingReportFormat): string {
  if (format === 'pdf') return 'application/pdf'
  if (format === 'csv') return 'text/csv'
  if (format === 'html') return 'text/html'
  return 'application/json'
}

export function accountingFallbackExportName(type: string, format: AccountingReportFormat): string {
  return safeAccountingDownloadName(`accounting-${type}.${format}`)
}

export function accountingReportExportMessageId(errorCode?: string, status?: number): string {
  if (errorCode === 'REPORT_TOO_LARGE') return 'ACCOUNTING.REPORTS.ERR_TOO_LARGE'
  if (errorCode === 'REPORT_RECONCILIATION_FAILED') return 'ACCOUNTING.REPORTS.ERR_RECONCILIATION'
  if (errorCode === 'PDF_RENDERER_UNAVAILABLE') return 'ACCOUNTING.REPORTS.ERR_PDF_UNAVAILABLE'
  if (errorCode === 'INVALID_REPORT_TYPE') return 'ACCOUNTING.REPORTS.ERR_INVALID_TYPE'
  if (errorCode === 'REPORT_TYPE_FORBIDDEN') return 'ACCOUNTING.REPORTS.ERR_UNAUTHORIZED'
  if (status === 401 || status === 403) return 'ACCOUNTING.REPORTS.ERR_UNAUTHORIZED'
  if (status === 413) return 'ACCOUNTING.REPORTS.ERR_TOO_LARGE'
  if (status === 503) return 'ACCOUNTING.REPORTS.ERR_PDF_UNAVAILABLE'
  return 'ACCOUNTING.ERROR'
}

export type AccountingExportBlobResult =
  | {kind: 'pdf'; blob: Blob}
  | {kind: 'text'; text: string}
  | {kind: 'error'; error: string; errorCode?: string}

function decodeBlobUtf8(bytes: Uint8Array): string {
  if (typeof TextDecoder !== 'undefined') return new TextDecoder('utf-8').decode(bytes)
  let text = ''
  bytes.forEach((code) => {
    text += String.fromCharCode(code)
  })
  return text
}

function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer))
  }
  if (typeof Response !== 'undefined') {
    return new Response(blob).arrayBuffer().then((buffer) => new Uint8Array(buffer))
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

export async function inspectAccountingExportBlob(
  blob: Blob,
  contentType?: string
): Promise<AccountingExportBlobResult> {
  const type = String(contentType || blob.type || '').toLowerCase()
  const bytes = await readBlobBytes(blob)
  let head = ''
  const headLength = Math.min(24, bytes.length)
  for (let i = 0; i < headLength; i += 1) head += String.fromCharCode(bytes[i])
  head = head.trim()
  if (head.startsWith('%PDF')) {
    return {kind: 'pdf', blob: type.includes('pdf') ? blob : new Blob([blob], {type: 'application/pdf'})}
  }
  const looksJson = type.includes('json') || head.startsWith('{') || head.startsWith('[')
  if (looksJson) {
    try {
      const json = JSON.parse(decodeBlobUtf8(bytes)) as {
        success?: boolean
        error?: string
        errorCode?: string
        message?: unknown
      }
      const error =
        typeof json.message === 'string'
          ? json.message
          : json.message && typeof json.message === 'object'
            ? String(
                (json.message as {fr?: string; en?: string}).fr ||
                  (json.message as {fr?: string; en?: string}).en ||
                  ''
              )
            : ''
      if (json.success === false || json.error || json.errorCode || error) {
        return {
          kind: 'error',
          error: error || json.error || 'Accounting request failed',
          errorCode: json.errorCode,
        }
      }
    } catch {
      return {kind: 'error', error: 'Accounting request failed'}
    }
  }
  if (type.includes('pdf')) {
    return {kind: 'error', error: 'Accounting request failed', errorCode: 'PDF_RENDERER_UNAVAILABLE'}
  }
  return {kind: 'text', text: decodeBlobUtf8(bytes)}
}

export function isSupportedReportType(type: string): type is AccountingReportType {
  return [
    'daily',
    'monthly',
    'academic-year',
    'establishment',
    'group',
    'payment-journal',
    'expense-journal',
    'ticket-print',
    'outstanding',
    'cashflow',
  ].includes(type)
}

function numericBucketValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && Number.isFinite(Number(value))) return Number(value)
  if (value && typeof value === 'object') {
    const row = value as Record<string, unknown>
    const nested = Number(row.total ?? row.totalTtc ?? row.paid ?? row.value ?? row.count)
    return Number.isFinite(nested) ? nested : null
  }
  return null
}

export function bucketEntries(bucket?: unknown): Array<{label: string; value: number}> {
  if (!bucket) return []
  if (Array.isArray(bucket)) {
    return bucket
      .map((row) => {
        if (!row || typeof row !== 'object') return null
        const item = row as Record<string, unknown>
        const rawLabel = item.paymentMethod ?? item.establishmentId ?? item.category ?? item.status ?? item.label
        const label = rawLabel == null || rawLabel === '' ? '—' : String(rawLabel)
        const value = numericBucketValue(item)
        if (value == null) return null
        return {label, value}
      })
      .filter((row): row is {label: string; value: number} => Boolean(row))
  }
  if (typeof bucket !== 'object') return []
  return Object.entries(bucket as Record<string, unknown>)
    .map(([label, value]) => {
      const numeric = numericBucketValue(value)
      if (numeric == null) return null
      return {label, value: numeric}
    })
    .filter((row): row is {label: string; value: number} => Boolean(row))
}

export function auditActorLabel(actor: unknown): string {
  if (!actor) return '—'
  if (typeof actor === 'string') return actor
  if (typeof actor === 'object') {
    const row = actor as {firstName?: string; lastName?: string; email?: string; _id?: string}
    const name = `${row.firstName || ''} ${row.lastName || ''}`.trim()
    return name || row.email || row._id || '—'
  }
  return '—'
}

export function auditEstablishmentLabel(row: {
  establishmentName?: string | null
  establishmentId?: string | null
  establishement?: unknown
}): string {
  if (row.establishmentName) return row.establishmentName
  if (row.establishmentId) return String(row.establishmentId)
  const est = row.establishement
  if (!est) return '—'
  if (typeof est === 'string') return est
  if (typeof est === 'object') {
    const item = est as {name?: string; nameFr?: string; _id?: string}
    return item.nameFr || item.name || item._id || '—'
  }
  return '—'
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function readPath(row: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    const record = asRecord(current)
    if (!record) return undefined
    return record[key]
  }, row)
}

function firstString(row: Record<string, unknown>, paths: string[]): string {
  for (const path of paths) {
    const value = readPath(row, path)
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

function firstMetric(row: Record<string, unknown>, paths: string[]): number | undefined {
  for (const path of paths) {
    const numeric = accountingMetricValue(readPath(row, path))
    if (numeric !== undefined) return numeric
  }
  return undefined
}

function personName(value: unknown): string {
  const record = asRecord(value)
  if (!record) return typeof value === 'string' ? value.trim() : ''
  const combined = `${record.firstName || record.prenom || ''} ${record.lastName || record.nom || ''}`.trim()
  if (combined) return combined
  const named = record.name || record.fullName || record.label
  return typeof named === 'string' ? named.trim() : ''
}

function extractRawOutstandingRows(envelope: AccountingReportEnvelope | Record<string, unknown> | null | undefined): unknown[] {
  if (!envelope) return []
  const record = asRecord(envelope)
  if (!record) return []
  const dataRecord = asRecord(record.data)
  const reportRecord = asRecord(record.report)
  const candidates = [
    record.rows,
    record.outstanding,
    record.charges,
    record.studentCharges,
    record.items,
    dataRecord?.rows,
    dataRecord?.outstanding,
    dataRecord?.charges,
    dataRecord?.items,
    reportRecord?.rows,
    record.data,
  ]
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue
    if (candidate.length === 0) return []
    const first = asRecord(candidate[0])
    if (first && Array.isArray(first.charges)) {
      return candidate.flatMap((item, index) => {
        const parent = asRecord(item) || {}
        const charges = Array.isArray(parent.charges) ? parent.charges : []
        return charges.map((charge, chargeIndex) => ({
          ...(asRecord(charge) || {}),
          student: parent.student || parent.studentName || parent.name,
          establishmentName: parent.establishmentName || parent.establishment,
          classroom: parent.classroom || parent.classroomName,
          academicYear: parent.academicYear,
          _parentId: parent.id || parent._id || index,
          _chargeIndex: chargeIndex,
        }))
      })
    }
    if (typeof candidate[0] === 'object') return candidate
  }
  return []
}

function dueDateStatus(dueDate: string): AccountingOverdueStatus {
  if (!dueDate) return 'unknown'
  const parsed = Date.parse(dueDate.length === 10 ? `${dueDate}T00:00:00` : dueDate)
  if (Number.isNaN(parsed)) return 'unknown'
  const due = new Date(parsed)
  const today = new Date()
  const dueDay = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate())
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  if (dueDay < todayDay) return 'overdue'
  if (dueDay > todayDay) return 'upcoming'
  return 'current'
}

export function outstandingOverdueStatus(row: Record<string, unknown>, dueDate: string): AccountingOverdueStatus {
  const overdueFlag = row.overdue ?? row.isOverdue ?? row.is_overdue ?? row.late
  if (overdueFlag === true) return 'overdue'
  if (overdueFlag === false) {
    const notYet = row.notYetDue ?? row.isNotYetDue
    if (notYet === true) return 'upcoming'
    const fromDate = dueDateStatus(dueDate)
    return fromDate === 'overdue' ? 'current' : fromDate
  }
  const status = String(row.overdueStatus ?? row.status ?? row.dueStatus ?? row.state ?? '').toLowerCase()
  if (status === 'overdue' || status === 'late' || status === 'en_retard' || status === 'en retard') return 'overdue'
  if (status === 'notyetdue' || status === 'not_yet_due' || status === 'upcoming' || status === 'a_venir' || status === 'à venir') {
    return 'upcoming'
  }
  if (status === 'current' || status === 'due' || status === 'today') return 'current'
  return dueDateStatus(dueDate)
}

export function mapOutstandingRow(raw: unknown, index: number): AccountingOutstandingRow {
  const row = asRecord(raw) || {}
  const studentName =
    firstString(row, ['studentName', 'student', 'payerName', 'guestPayerName', 'student.fullName', 'student.name']) ||
    personName(row.student) ||
    personName(row.user) ||
    '—'
  const establishmentName =
    firstString(row, [
      'establishmentName',
      'establishment',
      'establishementName',
      'establishment.nameFr',
      'establishment.name',
    ]) || '—'
  const classroomName =
    firstString(row, ['classroomName', 'classroom', 'className', 'classroom.name', 'classe']) || '—'
  const academicYear = firstString(row, ['academicYear', 'year', 'anneeScolaire']) || '—'
  const paymentCategory =
    firstString(row, ['paymentCategory', 'category', 'paymentType', 'chargeType', 'feeType']) || '—'
  const billingPeriod =
    firstString(row, ['billingPeriod', 'period', 'periodKey', 'periodLabel', 'month', 'billingMonth']) || '—'
  const dueDate = firstString(row, ['dueDate', 'due_date', 'deadline', 'dateEcheance']).slice(0, 10)
  return {
    id: firstString(row, ['id', '_id', 'chargeId']) || `outstanding-${index}`,
    studentName,
    establishmentName,
    classroomName,
    academicYear,
    paymentCategory,
    billingPeriod,
    dueDate,
    amountDue: firstMetric(row, ['amountDue', 'due', 'billedAmount', 'chargeAmount', 'amount']),
    allocatedPayments: firstMetric(row, [
      'allocatedPayments',
      'allocated',
      'amountAllocated',
      'allocatedTotal',
      'paid',
      'amountPaid',
    ]),
    remaining: firstMetric(row, ['remaining', 'amountRemaining', 'outstanding', 'unpaid', 'balance']),
    overdueStatus: outstandingOverdueStatus(row, dueDate),
  }
}

export function isOutstandingReportPaginated(
  envelope: AccountingReportEnvelope | Record<string, unknown> | null | undefined
): boolean {
  if (!envelope) return false
  const record = asRecord(envelope)
  if (!record) return false
  const pagination = asRecord(record.pagination)
  const rows = extractRawOutstandingRows(envelope)
  const total = accountingMetricValue(pagination?.total ?? record.total)
  const totalPages = accountingMetricValue(pagination?.totalPages ?? record.totalPages)
  const hasMore = pagination?.hasMore === true || pagination?.nextPage != null
  if (hasMore) return true
  if (totalPages !== undefined && totalPages > 1) return true
  if (total !== undefined && total > rows.length && (record.page != null || record.limit != null || pagination)) {
    return true
  }
  return false
}

function pickTotals(envelope: AccountingReportEnvelope | Record<string, unknown> | null | undefined): AccountingOutstandingTotals {
  const record = asRecord(envelope)
  const totals = asRecord(record?.totals) || asRecord(record?.summary) || {}
  const impayes = asRecord(totals.impayes) || asRecord(asRecord(record?.summary)?.impayes)
  return {
    amountDue: firstMetric(totals, ['amountDue', 'due', 'billedAmount', 'totalDue']),
    allocatedPayments: firstMetric(totals, ['allocatedPayments', 'allocated', 'amountAllocated', 'paid']),
    remaining:
      firstMetric(totals, ['remaining', 'amountRemaining', 'outstanding', 'unpaid']) ??
      accountingMetricValue(impayes?.total),
  }
}

function sumDefined(rows: AccountingOutstandingRow[], key: keyof Pick<AccountingOutstandingRow, 'amountDue' | 'allocatedPayments' | 'remaining'>): number | undefined {
  let sum = 0
  let seen = false
  for (const row of rows) {
    const value = row[key]
    if (value === undefined) continue
    seen = true
    sum += value
  }
  return seen ? Math.round(sum * 1000) / 1000 : undefined
}

export function normalizeOutstandingReport(
  envelope: AccountingReportEnvelope | Record<string, unknown> | null | undefined
): AccountingOutstandingReportView {
  const rows = extractRawOutstandingRows(envelope).map((row, index) => mapOutstandingRow(row, index))
  const backendTotals = pickTotals(envelope)
  const summary = (asRecord(envelope)?.summary as AccountingSummary | undefined) || undefined
  return {
    rows,
    paginated: isOutstandingReportPaginated(envelope),
    totals: {
      amountDue: backendTotals.amountDue ?? sumDefined(rows, 'amountDue'),
      allocatedPayments: backendTotals.allocatedPayments ?? sumDefined(rows, 'allocatedPayments'),
      remaining: backendTotals.remaining ?? sumDefined(rows, 'remaining'),
    },
    summary,
  }
}

export function outstandingRowsToCsv(rows: AccountingOutstandingRow[]): string {
  const header = [
    'student',
    'establishment',
    'classroom',
    'academicYear',
    'paymentCategory',
    'billingPeriod',
    'dueDate',
    'amountDue',
    'allocatedPayments',
    'remaining',
    'overdueStatus',
  ]
  const lines = rows.map((row) =>
    [
      row.studentName,
      row.establishmentName,
      row.classroomName,
      row.academicYear,
      row.paymentCategory,
      row.billingPeriod,
      row.dueDate,
      row.amountDue ?? '',
      row.allocatedPayments ?? '',
      row.remaining ?? '',
      row.overdueStatus,
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(';')
  )
  return `\uFEFF${[header.join(';'), ...lines].join('\n')}`
}

export type AccountingPeriodShortcut = 'today' | 'month' | 'quarter' | 'year' | 'custom'

function isoLocalDate(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function accountingPeriodShortcutRange(
  shortcut: Exclude<AccountingPeriodShortcut, 'custom'>,
  now = new Date()
): {dateFrom: string; dateTo: string} {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (shortcut === 'today') return {dateFrom: isoLocalDate(start), dateTo: isoLocalDate(start)}
  if (shortcut === 'month') {
    return {
      dateFrom: isoLocalDate(new Date(start.getFullYear(), start.getMonth(), 1)),
      dateTo: isoLocalDate(new Date(start.getFullYear(), start.getMonth() + 1, 0)),
    }
  }
  if (shortcut === 'quarter') {
    const quarterStart = Math.floor(start.getMonth() / 3) * 3
    return {
      dateFrom: isoLocalDate(new Date(start.getFullYear(), quarterStart, 1)),
      dateTo: isoLocalDate(new Date(start.getFullYear(), quarterStart + 3, 0)),
    }
  }
  return {
    dateFrom: isoLocalDate(new Date(start.getFullYear(), 0, 1)),
    dateTo: isoLocalDate(new Date(start.getFullYear(), 11, 31)),
  }
}

export function detectAccountingPeriodShortcut(
  dateFrom: string,
  dateTo: string,
  now = new Date()
): AccountingPeriodShortcut {
  if (!dateFrom || !dateTo) return 'custom'
  for (const shortcut of ['today', 'month', 'quarter', 'year'] as const) {
    const range = accountingPeriodShortcutRange(shortcut, now)
    if (range.dateFrom === dateFrom && range.dateTo === dateTo) return shortcut
  }
  return 'custom'
}

export function accountingReportTableRows(
  report?: AccountingReportEnvelope | null
): Record<string, unknown>[] {
  if (!report) return []
  const nested = asRecord(report.data)
  const candidates: unknown[] = [
    report.rows,
    report.outstanding,
    report.payments,
    report.expenses,
    report.tickets,
    report.items,
    report.charges,
    report.studentCharges,
    nested?.rows,
    nested?.items,
    nested?.payments,
    nested?.expenses,
    nested?.days,
    nested?.series,
  ]
  for (const list of candidates) {
    if (Array.isArray(list) && list.length) {
      return list.filter((row): row is Record<string, unknown> => Boolean(asRecord(row)))
    }
  }
  if (Array.isArray(report.data)) {
    return report.data.filter((row): row is Record<string, unknown> => Boolean(asRecord(row)))
  }
  return []
}

export function accountingReportOperationCount(
  report?: AccountingReportEnvelope | null
): number | undefined {
  if (!report) return undefined
  if (typeof report.total === 'number' && Number.isFinite(report.total)) return report.total
  if (typeof report.pagination?.total === 'number' && Number.isFinite(report.pagination.total)) {
    return report.pagination.total
  }
  const receipts = report.summary?.encaissements?.count
  const expenses = report.summary?.depenses?.recordedCount
  if (typeof receipts === 'number' || typeof expenses === 'number') {
    return (Number(receipts) || 0) + (Number(expenses) || 0)
  }
  const tickets = report.summary?.tickets?.issued
  if (typeof tickets === 'number' && Number.isFinite(tickets)) return tickets
  const rows = accountingReportTableRows(report)
  return rows.length || undefined
}

export function accountingComparableChange(
  current?: number,
  previous?: number
): number | undefined {
  if (
    typeof current !== 'number' ||
    typeof previous !== 'number' ||
    !Number.isFinite(current) ||
    !Number.isFinite(previous) ||
    previous === 0
  ) {
    return undefined
  }
  return ((current - previous) / Math.abs(previous)) * 100
}

export function accountingReportPriorSummary(
  report?: AccountingReportEnvelope | null
): AccountingSummary | undefined {
  const extra = report as (AccountingReportEnvelope & {comparison?: {previous?: AccountingSummary}}) | null
  return extra?.comparison?.previous
}

export function accountingKpiMismatches(
  reportSummary?: AccountingSummary | null,
  dashboardSummary?: AccountingSummary | null
): Array<{id: string; report?: number; dashboard?: number}> {
  if (!reportSummary || !dashboardSummary) return []
  const pairs: Array<{id: string; report?: number; dashboard?: number}> = [
    {
      id: 'encaissements.totalReceived',
      report: reportSummary.encaissements?.totalReceived,
      dashboard: dashboardSummary.encaissements?.totalReceived,
    },
    {
      id: 'depenses.paidTtc',
      report: reportSummary.depenses?.paidTtc,
      dashboard: dashboardSummary.depenses?.paidTtc,
    },
    {
      id: 'tresorerie.netCashMovement',
      report: reportSummary.tresorerie?.netCashMovement,
      dashboard: dashboardSummary.tresorerie?.netCashMovement,
    },
  ]
  return pairs.filter((row) => {
    if (row.report == null || row.dashboard == null) return false
    return Math.abs(row.report - row.dashboard) > 0.0005
  })
}

export function accountingReportCashSeries(
  report?: AccountingReportEnvelope | null
): Array<{label: string; receipts?: number; expenses?: number; net?: number}> {
  const nested = asRecord(report?.data)
  const series = nested?.series || nested?.days || nested?.daily
  if (Array.isArray(series) && series.length) {
    const points: Array<{label: string; receipts?: number; expenses?: number; net?: number}> = []
    series.forEach((item) => {
      const row = asRecord(item)
      if (!row) return
      const label = String(row.label || row.date || row.day || row.period || '')
      points.push({
        label: label || '—',
        receipts: accountingMetricValue(row.receipts ?? row.encaissements ?? row.inflows),
        expenses: accountingMetricValue(row.expenses ?? row.depenses ?? row.outflows),
        net: accountingMetricValue(row.net ?? row.mouvement),
      })
    })
    return points
  }
  const summary = report?.summary
  if (!summary) return []
  return [
    {
      label: 'period',
      receipts: summary.encaissements?.totalReceived,
      expenses: summary.depenses?.paidTtc,
      net: summary.tresorerie?.netCashMovement,
    },
  ]
}

export const ACCOUNTING_GENERAL_LEDGER_READY = false

export const ACCOUNTING_LEDGER_CAPABILITY_GAPS = [
  {id: 'chartOfAccounts', available: false},
  {id: 'fiscalPeriods', available: false},
  {id: 'journalDefinitions', available: false},
  {id: 'balancedDebitCredit', available: false},
  {id: 'postingAndReversals', available: false},
  {id: 'openingBalances', available: false},
  {id: 'accountMappings', available: false},
  {id: 'periodClosing', available: false},
  {id: 'immutablePostedHistory', available: false},
] as const

export type AccountingReportCatalogGroup = 'operational' | 'party' | 'tax' | 'ledger'

export interface AccountingReportCatalogItem {
  id: string
  type?: AccountingReportType
  group: AccountingReportCatalogGroup
  available: boolean
}

function isRestrictedExpenseJournalAccountingRole(role: string | number | undefined | null): boolean {
  const numeric = Number(role)
  if (numeric === 6 || numeric === 9) return true
  const named = String(role || '').toLowerCase().trim()
  return (
    named === 'finance' ||
    named === 'finance_manager' ||
    named === 'role_finance' ||
    named === 'accountant' ||
    named === 'comptable' ||
    named === 'role_accountant'
  )
}

export function accountingReportCatalogForRole(
  role: string | number | undefined | null
): AccountingReportCatalogItem[] {
  if (isRestrictedExpenseJournalAccountingRole(role)) {
    return ACCOUNTING_REPORT_CATALOG.filter((item) => item.type === 'expense-journal' && item.available)
  }
  return ACCOUNTING_REPORT_CATALOG
}

export function accountingAuthorizedReportType(
  role: string | number | undefined | null,
  requested?: string | null
): AccountingReportType {
  if (isRestrictedExpenseJournalAccountingRole(role)) return 'expense-journal'
  if (requested && isSupportedReportType(requested)) return requested
  return 'expense-journal'
}

const EXPENSE_AUDIT_ENTITY = /EXPENSE|DEPENSE|TEJ|FOLLOW.?UP|INVOICE|FACTURE|WITHHOLD|RETENUE|ATTACHMENT|PIECE|SUPPLIER|FOURNISSEUR/i
const NON_EXPENSE_AUDIT_ENTITY = /STUDENT|TICKET|RECEIVABLE|RECEIPT|ENCAISSE|PAYROLL|SALARY|IMPA[YÈ]E/i

export function isExpenseRelatedAccountingAuditRow(row: AccountingAuditRow | null | undefined): boolean {
  if (!row) return false
  const entity = String(row.entityType || '')
  const action = String(row.action || '')
  const hay = `${entity} ${action}`.trim()
  if (!hay) return true
  if (NON_EXPENSE_AUDIT_ENTITY.test(hay) && !EXPENSE_AUDIT_ENTITY.test(hay)) return false
  if (entity) return EXPENSE_AUDIT_ENTITY.test(entity)
  return EXPENSE_AUDIT_ENTITY.test(action)
}

export const ACCOUNTING_REPORT_CATALOG: AccountingReportCatalogItem[] = [
  {id: 'daily', type: 'daily', group: 'operational', available: true},
  {id: 'monthly', type: 'monthly', group: 'operational', available: true},
  {id: 'academic-year', type: 'academic-year', group: 'operational', available: true},
  {id: 'payment-journal', type: 'payment-journal', group: 'operational', available: true},
  {id: 'expense-journal', type: 'expense-journal', group: 'operational', available: true},
  {id: 'cashflow', type: 'cashflow', group: 'operational', available: true},
  {id: 'ticket-print', type: 'ticket-print', group: 'operational', available: true},
  {id: 'establishment', type: 'establishment', group: 'operational', available: true},
  {id: 'group', type: 'group', group: 'operational', available: true},
  {id: 'outstanding', type: 'outstanding', group: 'party', available: true},
  {id: 'supplier-statement', group: 'party', available: false},
  {id: 'supplier-balance', group: 'party', available: false},
  {id: 'unpaid-supplier-invoices', group: 'party', available: false},
  {id: 'supplier-payments', group: 'party', available: false},
  {id: 'customer-statement', group: 'party', available: false},
  {id: 'payment-delay', group: 'party', available: false},
  {id: 'withholding', group: 'tax', available: false},
  {id: 'tej', group: 'tax', available: false},
  {id: 'vat', group: 'tax', available: false},
  {id: 'tax-documents', group: 'tax', available: false},
  {id: 'journal', group: 'ledger', available: false},
  {id: 'general-ledger', group: 'ledger', available: false},
  {id: 'trial-balance', group: 'ledger', available: false},
  {id: 'supplier-auxiliary', group: 'ledger', available: false},
  {id: 'customer-auxiliary', group: 'ledger', available: false},
  {id: 'income-statement', group: 'ledger', available: false},
  {id: 'balance-sheet', group: 'ledger', available: false},
  {id: 'bank-journal', group: 'ledger', available: false},
  {id: 'cash-journal', group: 'ledger', available: false},
]

export function isOutstandingEnvelope(envelope: AccountingReportEnvelope | null | undefined): boolean {
  if (!envelope) return false
  if (envelope.type === 'outstanding') return true
  if (envelope.format && envelope.format !== 'json') return false
  const rows = extractRawOutstandingRows(envelope)
  if (!rows.length) return envelope.type == null
  const sample = asRecord(rows[0])
  if (!sample) return false
  return [
    'remaining',
    'amountDue',
    'dueDate',
    'studentName',
    'student',
    'allocatedPayments',
    'overdue',
  ].some((key) => key in sample)
}
